import countries from 'i18n-iso-countries';
import { InjectableClass } from '@fino/lib-injection';
import {
  TYPES,
  MOBILE_FULLSCREEN_IGNORE_TYPES,
  IGNORE_DATA_TYPES,
  SHARED_GRID_COL_TYPES,
  SHARED_MIN_GRID,
  GRID,
} from './form-fields.constants';

export class FormFieldsController extends InjectableClass {
  constructor(
    $scope,
    $timeout,
    $injector,
    dataGenerator,
    fdFormFields,
    fdFormFieldsService,
    fdFormFieldsDescriptionTemplatePath,
    countries
  ) {
    'ngInject';
    super({
      $scope,
      $timeout,
      $injector,
      dataGenerator,
      fdFormFields,
      fdFormFieldsService,
      fdFormFieldsDescriptionTemplatePath,
      countries,
    });

    this.inheritWatchers = {};
    this.modifierWatchers = [];
    this.expandableDepth = 0;
  }

  set hide(value) {
    this.resetServiceRegistration(!value);
    this.hidden = value;
  }

  $onInit() {}

  $onChanges(changes) {
    const newFieldGroup = this.getChanges(changes.fieldGroups);
    const newLanguageOptions = this.getChanges(changes.languageOptions);
    if (newFieldGroup) {
      this.fieldGroupsChanged(newFieldGroup);
    }
    if (newLanguageOptions) {
      this.languageOptionsChanged(newLanguageOptions);
    }
  }

  $onDestroy() {
    this.reset();
  }

  getChanges(field) {
    return (
      field && field.currentValue !== field.previousValue && field.currentValue
    );
  }

  walkThroughMappedFields(action) {
    if (this.mappedGroups && this.mappedGroups.length) {
      this.mappedGroups.map((group) => {
        group.fieldDefinitions.map((fieldRow) => {
          fieldRow.fields.map(action);
        });
      });
    }
  }

  fieldGroupsChanged(fieldGroups) {
    this.reset();
    this.mappedGroups = this.mapFieldGroups(angular.copy(fieldGroups));
    this.hasDescriptions = !!this.mappedGroups.filter((fg) => {
      return fg.descriptionHeaderKey || fg.descriptionBodyKey;
    }).length;
    this.resetServiceRegistration(!this.hidden);
  }

  languageOptionsChanged(newLanguageOptions) {
    if (this.mappedGroups && this.mappedGroups.length) {
      this.walkThroughMappedFields((field) => {
        if (newLanguageOptions && field.onLanguageChanged) {
          field.onLanguageChanged(newLanguageOptions);
        }
      });
    }
  }

  getDepth() {
    return this.expandableDepth + 1;
  }

  mapFieldGroups(fieldGroups) {
    fieldGroups.forEach((group) => {
      let fieldClone = angular.copy(group.fieldDefinitions);
      group.fieldDefinitions = this.mapFields(fieldClone);
    });
    return fieldGroups;
  }

  mapFields(fields) {
    let mappedFields = fields.reduce((result, field) => {
      // TODO (AV): Discuss whether it's a good idea
      // to map conflictable checkboxes to radio.
      switch (field.type) {
        // case TYPES.RADIO:
        case TYPES.SELECT_ONE:
          return (result = this.createHeaderIfNecessary(field, result).concat(
            this.mapRadio(field)
          ));
        // case TYPES.CHECKBOX:
        case TYPES.SELECT_MANY:
          return (result = this.createHeaderIfNecessary(field, result).concat(
            this.mapCheckbox(field)
          ));
        case TYPES.DATE:
          return (result = this.createHeaderIfNecessary(field, result).concat([
            this.createDate(field),
          ]));
        case TYPES.TEXT:
          return (result = this.createHeaderIfNecessary(field, result).concat([
            this.createText(field),
          ]));
        case TYPES.LABEL:
          return (result = this.createHeaderIfNecessary(field, result).concat([
            this.createLabel(field),
          ]));
        case TYPES.LIST:
          return (result = this.createHeaderIfNecessary(field, result).concat([
            this.createList(field),
          ]));
        case TYPES.BUTTON:
          return (result = this.createHeaderIfNecessary(field, result).concat(
            this.createButton(field)
          ));
        case TYPES.SELECT:
          return (result = this.createHeaderIfNecessary(field, result).concat([
            this.createSelect(field),
          ]));
        case TYPES.COUNTRY_SELECT:
          return (result = this.createHeaderIfNecessary(field, result).concat([
            this.createCountrySelect(field),
          ]));
        case TYPES.UPLOAD_FILE:
          return (result = this.createHeaderIfNecessary(field, result).concat([
            this.createUpload(field),
          ]));
        case TYPES.SEPARATOR:
          return (result = this.createHeaderIfNecessary(field, result).concat([
            this.createSeparator(field),
          ]));
        case TYPES.INCLUDE:
          return (result = this.createHeaderIfNecessary(field, result).concat([
            this.createInclude(field),
          ]));
        case TYPES.MULTIPLIER:
          return (result = this.createHeaderIfNecessary(field, result).concat([
            this.createMultiplier(field),
          ]));
        default:
          return (result = this.createHeaderIfNecessary(field, result).concat([
            this.createDefault(field),
          ]));
      }
    }, []);

    return this.organizeInRows(mappedFields);
  }

  organizeInRows(mappedFields) {
    const rows = mappedFields.reduce(
      (result, field) => {
        field.gridCol = this.calculateGridCol(field);
        let lastRow = result[result.length - 1];
        let aggCol = lastRow.fields.reduce((a, b) => {
          return a + b.gridCol;
        }, 0);

        if (aggCol + field.gridCol <= GRID) {
          lastRow.fields.push(field);
          return result;
        }

        result.push({ fields: [field], expandableGroups: [] });
        return result;
      },
      [{ fields: [], expandableGroups: [] }]
    );

    rows.forEach(this.applyExpandables);

    return rows;
  }

  applyExpandables(row) {
    row.fields
      .filter((field) => {
        return field.expandable && !field.expandable.stickToParent;
      })
      .forEach((field) => {
        const { expandable, shouldExpand } = field;
        row.expandableGroups.push({
          expandables: [expandable],
          shouldExpand,
        });
        row.isExpanded = () => {
          return row.expandableGroups.some((eg) => eg.shouldExpand());
        };
        row.fullWidth = field.fullWidth;
      });
  }

  mapRadio(field) {
    return this.createRadioOptions(field);
  }

  mapCheckbox(field) {
    return this.createCheckboxOptions(field);
  }

  createHeaderIfNecessary(field, result) {
    if (field && field.options && field.options.showHeader) {
      let header = {
        type: 'title',
        title: `${this.translatePrefix}.${field.name}`,
      };
      if (field.options.showSubHeader) {
        header.body = `${this.translatePrefix}.${field.name}.body`;
      }
      return result.concat([header]);
    }
    return result;
  }

  sharedGridCol(field, itemLength) {
    let gridCol = Math.max(
      Math.floor((field.gridCol || GRID) / itemLength),
      SHARED_MIN_GRID
    );
    if (!this.smallestGridCol || this.smallestGridCol > gridCol) {
      this.smallestGridCol = gridCol;
    }
  }

  createDefault(field) {
    return this.prependBaseOptions(field, {});
  }

  createRadioOptions(field) {
    let { name, options } = field;
    name += this.index || '';
    const typeOptions = this.fdFormFields.getTypeOptions(TYPES.RADIO);

    this.sharedGridCol(field, options.selectables.length);

    return options.selectables.map((item, index) => {
      if (index === 0 && !options.defaultSelection && !this.model[name]) {
        this.model[name] = options.defaultSelection || item.value;
      } else if (item.key === options.defaultSelection && !this.model[name]) {
        this.model[name] = item.value;
      }

      item.name = name;
      return this.prepareSelectableOptions(item, field, {
        type: 'radio',
        typeOptions,
        value: item.value,
        group: name,
        required: () => {
          return !this.model[name];
        },
        messages: this.createFieldMessages(field),
        shouldExpand: () => {
          return this.model[name] === item.value;
        },
      });
    });
  }

  createCheckboxOptions(field) {
    const { options, required } = field;
    const typeOptions = this.fdFormFields.getTypeOptions(TYPES.CHECKBOX);

    this.sharedGridCol(field, options.selectables.length);

    return options.selectables.map((item, index) => {
      const itemOptions = item.options || {};
      let name = typeOptions.combinedModel ? field.name : item.key;

      // Handle combinedModel and pre-checked model
      if (typeOptions.combinedModel) {
        if (!this.model[name]) {
          if (index === 0) {
            this.model[name] = {};
          }
          this.model[name][item.key] = itemOptions.checked || false;
        }
      } else {
        // Dont override model
        if (angular.isUndefined(this.model[name])) {
          this.model[name] = itemOptions.checked || false;
        }

        // Updates item options with model
        // It's a very strange and hacky way to manipulate parameters
        // Please make this better!
        if (item.options) {
          item.options.checked = this.model[name];
        }
      }

      const isActive = (selectable) => {
        if (typeOptions.combinedModel) {
          return this.model[name][selectable.key];
        }
        return this.model[selectable.key];
      };

      const checkbox = this.prepareSelectableOptions(item, field, {
        type: 'checkbox',
        typeOptions,
        name,
        value: item.key,
        required: () => {
          return required && !options.selectables.some(isActive);
        },
        messages: this.createFieldMessages(field),
        shouldExpand: () => {
          return isActive(item);
        },
      });

      // Add onChange handler to handle conflicts
      if (item.conflicts && item.conflicts.length) {
        checkbox.onChange = () => this.handleConflicts(item);
      }

      return checkbox;
    });
  }

  createDate(field) {
    const { languageOptions } = this;
    const { options, name } = field;

    if (/birthday/gi.test(name)) {
      options.datepickerMode = 'year';
    }

    options.dateFormat = languageOptions && languageOptions.dateFormat;
    options.bind = true;

    // Handle given default-, min-, and maxValue
    ['defaultValue', 'minValue', 'maxValue'].map((key) => {
      if (field[key]) {
        options[key] = new Date(field[key]);
      }
    });

    let dateField = this.prependBaseOptions(field, {
      type: 'date',
      typeOptions: options,
      required: () => {
        return field.required && !this.model[name];
      },
    });

    dateField.onLanguageChanged = (newLanguageOptions) => {
      dateField.typeOptions.dateFormat = newLanguageOptions.dateFormat;
    };

    return dateField;
  }

  createText(field) {
    return this.prependBaseOptions(field, {
      type: 'text',
    });
  }

  createLabel(field) {
    let label = this.prependBaseOptions(field, {
      type: 'label',
      main: `${this.translatePrefix}.${field.name}.label`,
    });
    if (field.hasSubLabel) {
      label.sub = `${this.translatePrefix}.${field.name}.label.sub`;
    }
    return label;
  }

  createList(field) {
    return this.prependBaseOptions(field, {
      type: 'list',
      listItems: field.textKeys,
      listItemStyle: field.listItemStyle,
    });
  }

  createMultiplier(field) {
    // TODO Solve dirty form name fix for radio buttons
    const { options } = field;
    const addFieldGroup = () => {
      let model = {};
      this.model[field.name].push(model);
    };
    const removeFieldGroup = (model) => {
      const index = this.model[field.name].indexOf(model);
      this.model[field.name].splice(index, 1);
    };

    // Check if data exist before override the model
    if (!Array.isArray(this.model[field.name])) {
      this.model[field.name] = [];
    }

    if (options.min) {
      for (let i = 0; i < options.min; i++) {
        addFieldGroup();
      }
    }

    return this.prependBaseOptions(field, {
      type: 'multiplier',
      add: () => {
        if (options.max && this.model[field.name].length === options.max) {
          return;
        }

        addFieldGroup();
      },
      remove: (model) => {
        if (options.min && this.model[field.name].length === options.min) {
          return;
        }

        removeFieldGroup(model);
      },
      icon: 'fa fa-plus',
      btnClass: 'btn-expandable',
      fieldGroups: [options.multipleGroup],
    });
  }

  createButton(field) {
    const { options } = field;

    // TODO (AV) This implementation yields only for the support of
    // a toggle button with expandable content. Any other button
    // usecase has yet to be defined and implemented.
    return options.selectables.map((item) => {
      const { expandable } = item;

      if (expandable) {
        // TODO (AV) Remove these values from the backend response
        delete expandable.descriptionHeaderKey;
        delete expandable.descriptionBodyKey;
      }

      const itemOptions = item.options || {};

      const isExpandable = !!expandable;

      const buttonLabel = `${this.translatePrefix}.${field.name}.label`;
      const buttonLabelExpanded = buttonLabel + '.expanded';
      const buttonIcon = 'fa fa-plus';
      const buttonIconExpanded = 'fa fa-minus';

      const button = this.prepareSelectableOptions(item, field, {
        type: 'button',
        name: field.name,
        label: buttonLabel,
        icon: buttonIcon,
        btnClass: 'btn-expandable',
        shouldExpand: () => expandable.expanded,
        fullWidth: isExpandable,
        descriptions: itemOptions.descriptions,
        descriptionsClass: `field-descriptions-${
          itemOptions.listPosition || 'bottom'
        }`,
      });

      // Add onClick handler to toggle expanded state
      button.onClick = () => {
        // Toggle item
        expandable.expanded = !expandable.expanded;

        // Toggle button label and icon
        button.label = expandable.expanded ? buttonLabelExpanded : buttonLabel;
        button.icon = expandable.expanded ? buttonIconExpanded : buttonIcon;

        // Toggle descriptions (hide when item is expanded)
        // TODO (AV) Improve this implementation, it seems to be pretty limited
        // to handle only one usecase with one-to-one relationsship between expandable
        // and its descriptions.
        if (expandable.fieldGroupId === itemOptions.listHide) {
          button.descriptionsHide = expandable.expanded;
        }
      };

      return button;
    });
  }

  createSeparator(field) {
    return this.prependBaseOptions(field, {
      type: 'separator',
    });
  }

  createInclude(field) {
    return this.prependBaseOptions(field, {
      type: 'include',
      file: field.name,
    });
  }

  createUpload(field) {
    return this.prependBaseOptions(field, {
      type: 'uploadFile',
    });
  }

  createSelect(field) {
    const { expandable, options } = field;
    const typeOptions = this.fdFormFields.getTypeOptions(TYPES.SELECT);
    const modelKey = (typeOptions && typeOptions.modelKey) || 'key';

    const selectables = options.selectables.map((s) => {
      return {
        key: s.key,
        value: options.showValues
          ? s.value
          : `${this.translatePrefix}.${s.key}`,
      };
    });

    if (options.preSelected && angular.isUndefined(this.model[field.name])) {
      const preSelected = selectables.find(
        (s) => s[modelKey] === options.preSelected
      );
      this.model[field.name] = preSelected[modelKey];
    }

    return this.createSelectField(field, {
      multiple: !!options.augmentable,
      selectables,
      modelKey: (typeOptions && typeOptions.modelKey) || 'key',
      removeSelected: false,
      expandable,
      shouldExpand: () => {
        return this.model[name] === expandable.value;
      },
    });
  }

  getCountriesByLanguage(lang) {
    let countryNames = countries.getNames(lang);
    if (this.fieldOptions && this.fieldOptions.filterCountries) {
      for (let key in countryNames) {
        if (this.fieldOptions.filterCountries.indexOf(key) < 0) {
          delete countryNames[key];
        }
      }
    }
    return Object.keys(countryNames).map((key) => {
      return {
        key,
        value: countryNames[key],
      };
    });
  }

  createCountrySelect(field) {
    const typeOptions = this.fdFormFields.getTypeOptions(TYPES.COUNTRY_SELECT);
    const priorities = (typeOptions && typeOptions.priorities) || [
      'GB',
      'DE',
      'IT',
      'FR',
      'TR',
    ];
    const lang = (this.languageOptions && this.languageOptions.lang) || 'de';
    const groupBy = (c) => {
      if (priorities.indexOf(c.key) > -1) {
        return '';
      }
      return ' ';
    };
    const groupFilter = ['', ' '];
    let countrySelect = this.createSelectField(field, {
      multiple: !!field.options.augmentable,
      groupBy,
      groupFilter,
      selectables: this.getCountriesByLanguage(lang),
      searchEnabled: true,
      modelKey: 'key',
      bind: true,
    });

    countrySelect.onLanguageChanged = (newLanguageOptions) => {
      countrySelect.typeOptions.selectables = this.getCountriesByLanguage(
        newLanguageOptions.lang
      );
    };
    return countrySelect;
  }

  createSelectField(field, typeOptions) {
    typeOptions = angular.extend(
      {
        selectKey: 'value',
      },
      typeOptions || {}
    );
    return this.prependBaseOptions(field, {
      type: 'select',
      typeOptions,
      placeholder: `${this.translatePrefix}.select.placeholder`,
      required: () => {
        if (!field.required) {
          return false;
        }
        if (typeOptions.multiple) {
          return !(this.model[field.name] && this.model[field.name].length);
        }
        return !this.model[field.name];
      },
      options: {
        selectables: typeOptions.selectables,
        multiple: typeOptions.multiple,
        modelKey: typeOptions.modelKey,
      },
    });
  }

  createFieldMessages(field) {
    const messages = {};
    const addToMessages = (msgId, id, value) => {
      messages[msgId] = {
        text: `${this.translatePrefix}.error.${msgId}.${field.name}`,
        values: { [id]: value },
      };
    };

    // Add required message
    if (field.required) {
      messages.required = `${this.translatePrefix}.error.required.${field.name}`;
    }

    // Add type specific messages
    switch (field.type) {
      case TYPES.DATE: {
        messages.date = `${this.translatePrefix}.error.dateInvalid`;
        break;
      }
      case TYPES.IBAN: {
        messages.ibanChecksum = `${this.translatePrefix}.error.required.${field.name}-checksum`;
        messages.ibanBankcode = `${this.translatePrefix}.error.required.${field.name}-bankcode`;
        messages.ibanAccountnumber = `${this.translatePrefix}.error.required.${field.name}-accountnumber`;
        break;
      }
    }

    if (field && field.options) {
      Object.keys(field.options).map((id) => {
        let msgId = id.toLowerCase();
        if (msgId === 'validate') {
          return field.options[id].split(',').forEach((v) => {
            addToMessages(v, id, v);
          });
        }
        if (id === 'controlType') {
          msgId = id = field.options[id].toLowerCase();
        }
        addToMessages(msgId, id, field.options[id]);
      });
    }

    return messages;
  }

  prependBaseOptions(field, result) {
    const { name, type, required, options, gridCol } = field;
    const typeOptions = this.fdFormFields.getTypeOptions(type);
    const modifier = typeOptions && typeOptions.modifier;
    if (modifier) {
      const modify = this.$injector.invoke(modifier.modify);
      this.modifierWatchers.push(
        this.$scope.$watch(
          () => this.model[name],
          () => {
            this.model[`${name}${modifier.appendix}`] = modify(
              this.model[name]
            );
          }
        )
      );
    }
    let placeholder, label;
    placeholder = label = `${this.translatePrefix}.${field.name}`;
    const messages = this.createFieldMessages(field);
    const doValidate = !this.manualValidation;
    const inheritPath = this.resolveInheritance(field);
    return angular.extend(
      {
        name,
        type,
        options,
        gridCol,
        required,
        messages,
        placeholder,
        label,
        doValidate,
        inheritPath,
      },
      result || {}
    );
  }

  prepareSelectableOptions(field, parent, result) {
    const { expandable, type, options } = field;
    const imageName = type === 'image' ? field.key : undefined;
    const label = `${this.translatePrefix}.${field.key}`;
    const index = parent.options.selectables.indexOf(field);
    const selectableLength = parent.options.selectables.length;
    let selectableOptions = this.prependBaseOptions(field, {
      label,
      placeholder: null,
      last: index === selectableLength - 1,
      gridBuffer: (smallesGridCol) => {
        return (parent.gridCol || GRID) - selectableLength * smallesGridCol;
      },
      imageName,
      expandable,
      hasExpandable: !!(expandable && expandable.stickToParent),
    });
    return angular.extend(selectableOptions, options, result || {});
  }

  handleConflicts(field) {
    return field.conflicts;
  }

  calculateGridCol(field) {
    if (field.gridCol) {
      return field.gridCol;
    }

    if (!field.block && SHARED_GRID_COL_TYPES.indexOf(field.type) > -1) {
      let gridCol = this.smallestGridCol;
      if (field.last) {
        gridCol += field.gridBuffer(gridCol);
      }
      return gridCol;
    }

    return GRID;
  }

  fieldClasses(field) {
    let fieldClass = '';

    if (MOBILE_FULLSCREEN_IGNORE_TYPES.indexOf(field.type) > -1) {
      fieldClass = `col-xs-${field.gridCol} ${field.type}-type`;
    } else {
      fieldClass = `col-xs-12 col-sm-${field.gridCol} ${field.type}-type`;
    }

    if (field.expandable) {
      fieldClass += ' field-container-expandable';
    }

    return fieldClass;
  }

  fillRandomData(options) {
    const ignore = (options && options.ignore) || [];
    this.walkThroughMappedFields((field) => {
      if (
        !this.model.hasOwnProperty(field.name) &&
        field.type &&
        IGNORE_DATA_TYPES.indexOf(field.type) === -1 &&
        ignore.indexOf(field.name) === -1
      ) {
        this.model[field.name] = this.getRandomData(field);
      }
    });
  }

  toggleDevMode() {
    this.walkThroughMappedFields((field) => {
      field.devMode = !field.devMode;
    });
  }

  validate(reset = false) {
    if (this.manualValidation) {
      this.walkThroughMappedFields((field) => {
        let hasPattern = !!field.options && !!field.options.pattern;
        let shouldValidate = !!field.options && !!field.options.validate;
        let needsValidation = field.required || hasPattern || shouldValidate;
        if (needsValidation && IGNORE_DATA_TYPES.indexOf(field.type) === -1) {
          field.doValidate = !reset;
        }
      });
    }
  }

  getRandomData(field) {
    const { type, name, value } = field;
    const options = angular.extend({}, field.options || {}, {
      type,
      name,
      value,
      minLength: 3,
      maxLength: 10,
    });

    return this.dataGenerator.generate(options);
  }

  onChange(field, previousValue) {
    if (angular.isFunction(field.onChange)) {
      let conflictables = field.onChange();

      // Handle conflictables only when the previous value is falsy
      if (!previousValue) {
        // Unset all conflictables
        conflictables.map((item) => {
          this.model[item] = false;
        });
      }
    }
    // Unbind inherit watcher as soon as the user enters some value
    this.unbindWatcherIfAvailable(field.inheritPath);
  }

  unbindWatcherIfAvailable(inheritPath) {
    const unwatch = this.inheritWatchers[inheritPath];
    if (unwatch) {
      unwatch();
      delete this.inheritWatchers[inheritPath];
    }
  }

  resolveInheritance(field) {
    if (!field.inherit) return null;

    const inheritPath = field.inherit.split('.');

    const getValue = () => {
      let value = this.inheritModel;
      inheritPath.forEach((p) => {
        if (!value) return;
        value = value[p];
      });
      return value;
    };
    const setValue = (value) => {
      this.model[field.name] = value;
    };

    // Watch on inherit path and set model when value changes
    const unwatch = this.$scope.$watch(getValue, setValue);
    this.inheritWatchers[inheritPath] = unwatch;
    return inheritPath;
  }

  reset() {
    this.mappedGroups = [];
    Object.keys(this.inheritWatchers).forEach(this.unbindWatcherIfAvailable);
    this.hasDescriptions = false;
    this.smallestGridCol = GRID;
    this.modifierWatchers.forEach((mw) => mw());
    this.modifierWatchers = [];
  }

  resetServiceRegistration(reregister = false) {
    if (!this.fdFormFieldsService) {
      return;
    }

    if (this.unregisterFromService) {
      this.unregisterFromService();
    }
    if (reregister && this.form) {
      this.unregisterFromService = this.fdFormFieldsService.register(
        this.form,
        {
          validators: this.validate.bind(this),
          fieldResetters: this.resetInputAndValidation.bind(this),
          randomDataFillers: this.fillRandomData.bind(this),
          devModeToggles: this.toggleDevMode.bind(this),
        }
      );
    }
  }

  resetInput() {
    this.model = angular.copy({});
    this.form.$setPristine();
    this.form.$setUntouched();
  }

  resetInputAndValidation() {
    this.resetInput();
    this.validate(true);
  }
}
